// ABOUTME: The fetch client for the talent's applications on pro-core: bearer token, Accept-Language, and the error
// ABOUTME: mapping that turns the flag-off 404 into a recognisable code instead of a crash.
import { ApiError, ResponseError } from 'utils/api';
import { env } from '../../constants';
import {
  APPLICATION_NOT_FOUND_ERROR_CODE,
  APPLICATIONS_API_URL,
  APPLICATIONS_UNAVAILABLE_ERROR_CODE
} from './constants';
import {
  ApplicationResponse,
  ApplicationsFilter,
  ApplicationsResponse,
  AppliedWeekUpdate,
  TalentApplication
} from './types';

const applicationUrl = (refId: string) =>
  `${env.VITE_API_BASE_URL}/${APPLICATIONS_API_URL}/${refId}`;

const talentHeaders = (token: string, language: string) => ({
  Authorization: `Bearer ${token}`,
  'Accept-Language': language
});

/**
 * The structured body behind a failed call, or an empty one when there is nothing to read: pro-core answers some
 * refusals without a body, and a screen must not fail on the way to telling somebody what went wrong.
 */
const readError = async (response: Response): Promise<ResponseError> => {
  try {
    const json = (await response.json()) as Partial<ResponseError>;
    return { code: json.code ?? '', values: json.values ?? {} };
  } catch {
    return { code: '', values: {} };
  }
};

/**
 * Every failure of these three calls, as an ApiError the screens branch on.
 *
 * A 404 arrives for two reasons and only the body tells them apart: `error.application.not-found` is a refId that is
 * not this talent's own (or no longer exists), and anything else is PRO_ATS being off for them, which both screens
 * show as their friendly off state. A 401 reloads first so the Auth0 provider renews the session, as the other
 * talent endpoints do. Everything else travels under the structured { code, values } body pro-core sent.
 */
const throwApplicationError = async (response: Response): Promise<never> => {
  if (response.status === 401) {
    window.location.reload();
  }

  const error = await readError(response);

  if (
    response.status === 404 &&
    error.code !== APPLICATION_NOT_FOUND_ERROR_CODE
  ) {
    throw new ApiError({
      code: APPLICATIONS_UNAVAILABLE_ERROR_CODE,
      values: {}
    });
  }

  throw new ApiError(error);
};

/**
 * pro-core serialises with non_empty, so a null or an empty list is no key at all: an application whose process shows
 * talents no step arrives without `steps`, and a list row without `canWithdraw`, `events` or `sharedReason`. The
 * screens are owed the documented shape, so the absent keys are put back here: the tracker empty, the rest null.
 */
const withSteps = (application: TalentApplication): TalentApplication => ({
  ...application,
  desiredStartDate: application.desiredStartDate ?? null,
  currentStepLabel: application.currentStepLabel ?? null,
  steps: application.steps ?? [],
  sharedReason: application.sharedReason ?? null,
  appliedAt: application.appliedAt ?? null,
  lastChangedAt: application.lastChangedAt ?? null,
  canWithdraw: application.canWithdraw ?? null,
  events: application.events ?? null,
  ...(application.employment
    ? {
        employment: {
          ...application.employment,
          beforeStart: application.employment.beforeStart ?? []
        }
      }
    : {})
});

/**
 * GET /talent/applications: the talent's own applications, newest first, each with its step tracker.
 *
 * PJL (#2106, while PRO_APPLY_V2 is on): without a filter the list leaves out the rows the talent hid, except those
 * that need them; `includeHidden=true` adds the hidden rows, `needsAction=true` keeps only the rows that need them.
 * Off, pro-core ignores both and answers today's list.
 *
 * `language` goes out as Accept-Language because the step labels and hints are rendered by the backend in the
 * request's language; the app keeps no step names of its own.
 */
export const fetchApplications = async (
  token: string,
  language: string,
  filter: ApplicationsFilter = {}
): Promise<TalentApplication[]> => {
  const query = new URLSearchParams();
  if (filter.includeHidden) query.set('includeHidden', 'true');
  if (filter.needsAction) query.set('needsAction', 'true');
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await fetch(
    `${env.VITE_API_BASE_URL}/${APPLICATIONS_API_URL}${suffix}`,
    {
      method: 'GET',
      headers: talentHeaders(token, language)
    }
  );

  if (!response.ok) {
    await throwApplicationError(response);
  }

  // pro-core serialises with non_empty: a talent with no applications gets no `data` key, and is owed the empty state.
  const json = (await response.json()) as Partial<ApplicationsResponse>;
  return (json.data ?? []).map(withSteps);
};

/** GET /talent/applications/{refId}: the same application the list shows, plus its timeline and canWithdraw. */
export const fetchApplication = async (
  token: string,
  language: string,
  refId: string
): Promise<TalentApplication> => {
  const response = await fetch(applicationUrl(refId), {
    method: 'GET',
    headers: talentHeaders(token, language)
  });

  if (!response.ok) {
    await throwApplicationError(response);
  }

  const json = (await response.json()) as ApplicationResponse;
  return withSteps(json.data);
};

/**
 * POST /talent/applications/{refId}/withdraw: the talent's own words, which the backend records on the transition and
 * shows the operator. The answer is the application as it reads after the move, so the screen needs no second read.
 */
export const withdrawApplication = async (
  token: string,
  language: string,
  refId: string,
  reasonText: string
): Promise<TalentApplication> => {
  const response = await fetch(`${applicationUrl(refId)}/withdraw`, {
    method: 'POST',
    headers: {
      ...talentHeaders(token, language),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ reasonText })
  });

  if (!response.ok) {
    await throwApplicationError(response);
  }

  const json = (await response.json()) as ApplicationResponse;
  return withSteps(json.data);
};

/**
 * PUT /talent/applications/{refId}/hidden (pro-core #2106): hide the application from the talent's own list, or show
 * it again. Only the talent's view changes; Zenjob and the company see nothing of it. Answers the row as it reads now,
 * in the list's shape. A refId that is not mine is `error.application.not-found`; PRO_APPLY_V2 off is the 404 the
 * screens read as "unavailable".
 */
export const setApplicationHidden = async (
  token: string,
  language: string,
  refId: string,
  hidden: boolean
): Promise<TalentApplication> => {
  const response = await fetch(`${applicationUrl(refId)}/hidden`, {
    method: 'PUT',
    headers: {
      ...talentHeaders(token, language),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ hidden })
  });

  if (!response.ok) {
    await throwApplicationError(response);
  }

  const json = (await response.json()) as ApplicationResponse;
  return withSteps(json.data);
};

/**
 * PUT /talent/applications/{refId}/availability (PJL draft, NOT on pro-core yet): the week sent with the application,
 * changed afterwards. pro-core is to keep the new snapshot and give the account manager a task, answering the
 * application as it reads now. Until that route exists (#2032's change-my-week, after #2106) this call answers 404,
 * which the sheet shows as "could not be saved"; only the mock serves it.
 */
export const updateAppliedWeek = async (
  token: string,
  language: string,
  refId: string,
  week: AppliedWeekUpdate
): Promise<TalentApplication> => {
  const response = await fetch(`${applicationUrl(refId)}/availability`, {
    method: 'PUT',
    headers: {
      ...talentHeaders(token, language),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(week)
  });

  if (!response.ok) {
    await throwApplicationError(response);
  }

  const json = (await response.json()) as ApplicationResponse;
  return withSteps(json.data);
};
